import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { HelpCircle, Zap, Star, Bell, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import QuizzyLogo from "./QuizzyLogo";
import PhoneMockup from "./PhoneMockup";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const a1x = useTransform(sx, (v) => v * 60);
  const a1y = useTransform(sy, (v) => v * 60);
  const a2x = useTransform(sx, (v) => v * -80);
  const a2y = useTransform(sy, (v) => v * -80);
  const a3x = useTransform(sx, (v) => v * 28);
  const a3y = useTransform(sy, (v) => v * 28);

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/notify", { email });
      toast.success(
        data.message === "already"
          ? "You're already on the list. Legend."
          : "You're in! We'll ping you the second we launch."
      );
      setEmail("");
    } catch {
      toast.error("Hmm, that email doesn't look right.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="coming-soon-banner"
      onMouseMove={onMove}
      className="grid-lines relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4"
    >
      <div className="animate-blob absolute -left-32 top-1/4 h-[420px] w-[420px] rounded-full bg-[#00F0FF]/12 blur-[120px]" />
      <div className="animate-blob absolute -right-32 bottom-1/4 h-[420px] w-[420px] rounded-full bg-[#FF007A]/12 blur-[120px]" style={{ animationDelay: "4s" }} />
      <div className="noise-overlay absolute inset-0" />

      <motion.div style={{ x: a1x, y: a1y }} className="absolute left-[12%] top-[20%] hidden md:block">
        <div className="glass animate-floaty flex h-20 w-20 items-center justify-center rounded-3xl">
          <HelpCircle size={34} className="text-[#00F0FF]" />
        </div>
      </motion.div>
      <motion.div style={{ x: a2x, y: a2y }} className="absolute right-[12%] top-[26%] hidden md:block">
        <div className="glass animate-floaty flex h-16 w-16 items-center justify-center rounded-2xl" style={{ animationDelay: "1s" }}>
          <Zap size={26} className="text-[#39FF14]" fill="#39FF14" />
        </div>
      </motion.div>
      <motion.div style={{ x: a2x, y: a1y }} className="absolute bottom-[18%] left-[16%] hidden md:block">
        <div className="glass animate-floaty flex h-14 w-14 items-center justify-center rounded-full" style={{ animationDelay: "2s" }}>
          <Star size={22} className="text-[#FF007A]" fill="#FF007A" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-14 lg:grid-cols-2">
        <div className="text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex justify-center lg:justify-start"
          >
            <QuizzyLogo size={64} />
          </motion.div>

          <p className="font-code mt-8 text-xs tracking-[0.35em] text-[#00F0FF]">
            SOMETHING EPIC IS LOADING
          </p>
          <h1 className="font-display mt-5 text-5xl font-extrabold leading-[1.02] sm:text-7xl lg:text-[5.2rem]">
            <MaskedLine delay={0.2}>
              <span className="text-glow-cyan">COMING</span>
            </MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-stroke-magenta">SOON</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mx-auto mt-6 max-w-md text-sm text-[#94A3B8] sm:text-base lg:mx-0"
          >
            Quizzy — the ultimate trivia battleground — is landing on Google Play.
            Free to play. Android only. Be first through the gates.
          </motion.p>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mx-auto mt-9 flex w-full max-w-md flex-col gap-3 sm:flex-row lg:mx-0"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@galaxy.com"
              data-testid="coming-soon-email-input"
              className="glass flex-1 rounded-full px-5 py-3.5 font-sub text-sm text-white placeholder:text-[#64748B] focus:border-[#00F0FF] focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="coming-soon-submit-button"
              className="btn-neon animate-pulse-ring flex items-center justify-center gap-2 rounded-full bg-[#00F0FF] px-7 py-3.5 font-sub text-sm font-bold text-[#08090E] disabled:opacity-60"
            >
              <Bell size={15} />
              {loading ? "Joining..." : "Notify Me"}
            </button>
          </motion.form>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          style={{ x: a3x, y: a3y }}
          className="hidden lg:block"
        >
          <PhoneMockup />
        </motion.div>
      </div>

      <Link
        to="/admin"
        data-testid="coming-soon-admin-link"
        className="absolute bottom-6 right-6 z-10 flex items-center gap-1.5 font-code text-[10px] tracking-widest text-[#64748B] transition-colors hover:text-[#00F0FF]"
      >
        <Lock size={11} /> ADMIN
      </Link>
    </div>
  );
}
